// One way to read data in every tab (4 Nov 2026), replacing the six copies of
// « useEffect + then + catch » the tabs had grown:
// - the first load shows the page's skeleton, never a blank page;
// - a refresh keeps what is on screen and says it is refreshing (stale while
//   revalidate), so a page never blinks back to grey;
// - a new subject (another home, another agency) drops the old one at once,
//   so the wrong home is never shown under the new name; a new filter (a
//   period) may keep the old figures, dimmed, until the new ones arrive;
// - answers that arrive out of order are ignored: only the latest request
//   counts, and nothing is set after the tab is gone;
// - a failed refresh keeps the data and raises `failed`; a failed first load
//   is an error the page can retry, with the button busy while it does.
// The state machine is the pure `step` below, tested on its own.
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { track } from './progress';

export interface RState<T> {
  data: T | null;
  /** Which subject `data` belongs to. */
  dataKey: number;
  error: string | null;
  errorKey: number;
  /** The latest request started, and the one still running (0 when none). */
  seq: number;
  inflight: number;
  at: Date | null;
}

export type RAction<T> =
  | { t: 'start'; seq: number }
  | { t: 'ok'; seq: number; key: number; data: T }
  | { t: 'fail'; seq: number; key: number; error: string }
  | { t: 'set'; fn: (d: T) => T };

export const initial = <T,>(): RState<T> => ({ data: null, dataKey: -1, error: null, errorKey: -1, seq: 0, inflight: 0, at: null });

export function step<T>(s: RState<T>, a: RAction<T>): RState<T> {
  switch (a.t) {
    case 'start': return { ...s, seq: a.seq, inflight: a.seq };
    case 'ok':
      if (a.seq < s.seq) return s; // an older answer, overtaken by a newer request
      return { ...s, data: a.data, dataKey: a.key, error: null, errorKey: -1, inflight: 0, at: new Date() };
    case 'fail':
      if (a.seq < s.seq) return s;
      return { ...s, error: a.error, errorKey: a.key, inflight: 0 };
    case 'set': return s.data === null ? s : { ...s, data: a.fn(s.data) };
  }
}

export interface Resource<T> {
  /** What to show: null only while nothing is known for this subject yet. */
  data: T | null;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  /** A read is running while data is already on screen. */
  refreshing: boolean;
  /** The data on screen belongs to the previous filter (keep: true only). */
  stale: boolean;
  /** The last refresh failed; the data on screen is the last good one. */
  failed: boolean;
  /** « Réessayer » is running after an error. */
  retrying: boolean;
  updatedAt: Date | null;
  /** A read the person asked for: it moves the bar at the top. */
  reload(): void;
  /** Changes the data on screen at once (an action that already succeeded). */
  set(fn: (d: T) => T): void;
}

/** The view a page reads, from the machine's state and the current subject. */
export function view<T>(s: RState<T>, key: number, keep: boolean): Omit<Resource<T>, 'reload' | 'set'> {
  const own = s.dataKey === key;
  const data = own || keep ? s.data : null;
  const error = s.errorKey === key ? s.error : null;
  return {
    data,
    status: data !== null ? 'ready' : error ? 'error' : 'loading',
    error,
    refreshing: data !== null && s.inflight > 0,
    stale: data !== null && !own,
    failed: data !== null && !!error,
    retrying: data === null && !!error && s.inflight > 0,
    updatedAt: own ? s.at : null,
  };
}

const sameDeps = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
const message = (e: unknown) => (e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e));

/**
 * Reads `load()` for the subject named by `key`, again whenever something in
 * `refresh` changes (silently), and on reload() (visibly). `load` null means
 * « nothing to read yet » (a closed panel).
 */
export function useResource<T>(load: (() => Promise<T>) | null, key: unknown[], opts: { refresh?: unknown[]; keep?: boolean } = {}): Resource<T> {
  const [s, dispatch] = useReducer(step<T>, undefined, initial<T>);
  // The subject's number moves when `key` changes (React's « information from previous renders » pattern).
  const [k, setK] = useState({ deps: key, id: 0 });
  let id = k.id;
  if (!sameDeps(k.deps, key)) { id = k.id + 1; setK({ deps: key, id }); }

  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; });
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const run = useCallback((subject: number, visible: boolean) => {
    const fn = loadRef.current;
    if (!fn) return;
    const n = ++seq.current;
    dispatch({ t: 'start', seq: n });
    const p = fn();
    (visible ? track(p) : p).then(
      (data) => { if (alive.current) dispatch({ t: 'ok', seq: n, key: subject, data }); },
      (e: unknown) => { if (alive.current) dispatch({ t: 'fail', seq: n, key: subject, error: message(e) }); },
    );
  }, []);

  // A new subject is a read someone is waiting for; a refresh of the same one is not.
  const shown = useRef(-1);
  const refresh = opts.refresh ?? [];
  useEffect(() => {
    if (!load) return;
    const fresh = shown.current !== id;
    shown.current = id;
    run(id, fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, !!load, run, ...refresh]);

  const reload = useCallback(() => run(id, true), [run, id]);
  const set = useCallback((fn: (d: T) => T) => dispatch({ t: 'set', fn }), []);
  return { ...view(s, id, !!opts.keep), reload, set };
}
